import { useCallback } from 'react';

export const useCanvasOperations = ({canvasState, canvasRef}) => {
  const {
    ctx, history, setHistory, scale, imageWidth, imageHeight,
    brushColor, brushWidth, isDrawing, setIsDrawing, isTextRegim,
    canvasText, setCanvasText, showCanvasText, setShowCanvasText,
    xY, setXY, canvasFont
  } = canvasState;

  const saveImageToHistory = useCallback(() => {
    const dataURL = canvasRef.current.toDataURL('image/png');
    setHistory(prevHistory => [...prevHistory, dataURL]);
  }, [canvasRef, setHistory]);

  const drawImageOnCanvas = useCallback((image) => {
    const canvas = canvasRef.current;
    const context = ctx;

    const imgWidth = image.width;
    const imgHeight = image.height;
    canvasState.setImageWidth(imgWidth);
    canvasState.setImageHeight(imgHeight);

    canvas.width = imgWidth;
    canvas.height = imgHeight;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, imgWidth, imgHeight);
    saveImageToHistory();
  }, [ctx, canvasRef, saveImageToHistory, canvasState]);

  const drawImageOnCanvas2 = useCallback((image) => {
    const context = ctx;
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    context.drawImage(image, 0, 0, imageWidth * scale, scale * imageHeight);
  }, [ctx, canvasRef, imageWidth, imageHeight, scale]);

  const getMousePos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
  }, [canvasRef]);

  const startDrawing = useCallback((e) => {
    if (isTextRegim) return;
    setIsDrawing(true);
    ctx.beginPath();
    const { offsetX, offsetY } = getMousePos(e);
    ctx.moveTo(offsetX - 16, offsetY + 20);
    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushWidth;
  }, [isTextRegim, setIsDrawing, ctx, getMousePos, brushColor, brushWidth]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    const { offsetX, offsetY } = getMousePos(e);
    ctx.lineTo(offsetX - 16, offsetY + 20);
    ctx.stroke();
  }, [isDrawing, ctx, getMousePos]);

  const stopDrawing = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);
      ctx.closePath();
      saveImageToHistory();
    }
  }, [isDrawing, setIsDrawing, ctx, saveImageToHistory]);

  const undoLastAction = useCallback(() => {
    if (history.length > 1) {
      const newHistory = history.slice(0, history.length - 1);
      canvasState.setHistory(newHistory);

      if (newHistory.length > 0) {
        const lastState = newHistory[newHistory.length - 1];
        const img = new Image();
        img.src = lastState;
        img.onload = () => {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          drawImageOnCanvas2(img);
        };
      } else {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
  }, [history, canvasState, ctx, canvasRef, drawImageOnCanvas2]);

  let isRedrawing = false;
  const redrawCanvas = useCallback((newScale) => {
    if (isRedrawing || history.length === 0) return;
    isRedrawing = true;
    const lastState = history[history.length - 1];
    const lastImage = new Image();
    lastImage.src = lastState;

    lastImage.onload = () => {
      canvasRef.current.width = imageWidth * newScale;
      canvasRef.current.height = imageHeight * newScale;
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(lastImage, 0, 0, imageWidth * newScale, imageHeight * newScale);
      isRedrawing = false;
    };
  }, [history, ctx, canvasRef, imageWidth, imageHeight]);

  const increaseScale = useCallback(() => {
    canvasState.setScale(prevScale => {
      const newScale = Math.min(prevScale + 0.1, 5);
      redrawCanvas(newScale);
      return newScale;
    });
  }, [canvasState, redrawCanvas]);

  const decreaseScale = useCallback(() => {
    canvasState.setScale(prevScale => {
      const newScale = Math.max(prevScale - 0.1, 0.1);
      redrawCanvas(newScale);
      return newScale;
    });
  }, [canvasState, redrawCanvas]);

  const addText = useCallback((e) => {
    if (!isTextRegim) return;
    if (!showCanvasText) {
      setShowCanvasText(true);
    }
    setXY(getMousePos(e));
  }, [isTextRegim, showCanvasText, setShowCanvasText, setXY, getMousePos]);

  const saveCanvasText = useCallback(() => {
    if (!canvasText) {
      setShowCanvasText(false);
      return;
    }
    const allStr = canvasText.split('\n');
    ctx.fillStyle = brushColor;
    ctx.font = canvasFont + "px Montserrat";
    allStr.forEach((el, i) => {
      ctx.fillText(el, xY.offsetX, xY.offsetY + 1.3 * i * canvasFont);
    });

    saveImageToHistory();
    setCanvasText('');
    setShowCanvasText(false);
  }, [canvasText, ctx, brushColor, canvasFont, xY, saveImageToHistory, setCanvasText, setShowCanvasText]);

  const changeCanvasFont = useCallback((value) => {
    if (!Number.isInteger(Number(value))) return;
    if (value < 10 || value > 50) return;
    canvasState.setCanvasFont(value);
  }, [canvasState]);

  return {
    saveImageToHistory,
    drawImageOnCanvas,
    drawImageOnCanvas2,
    getMousePos,
    startDrawing,
    draw,
    stopDrawing,
    undoLastAction,
    increaseScale,
    decreaseScale,
    addText,
    saveCanvasText,
    changeCanvasFont,
    redrawCanvas
  };
};